/**
 * Comparison Hub — the 6-column overlay of all rate sources against the
 * Part Rate DB, with:
 *   - filter bar (canonical vocabulary from the joined dataset)
 *   - one-paragraph AI insight regenerated for the current filter
 *   - aggregate stats (coverage, pairwise ratios vs the like-for-like column)
 *   - the row-level table with gap / not-covered markers and ratio badges
 *   - unmatched families + crosswalk mapping editor
 *   - Excel export of the current filter
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import {
  styles, ACCENT, SOURCE_LABELS, SOURCE_COLORS, StatCard, Spinner, ErrorBox,
  RatioBadge, SourceCell, fmtHours, fmtRatio, fmtInt, apiGet, apiSend, errMsg,
} from './rbShared';

const EMPTY_FILTERS = {
  description: '', material: '', sch: '', rating: '',
  sizeMin: '', sizeMax: '', search: '', minCoverage: '', onlyGapsIn: '', excludeReducers: false,
};

const TABLE_SOURCES = ['PART_RATE_DB', 'PART_RATE_DB_MCAA', 'OTTAV_61075', 'OTTAV_LATEST', 'MCAA', 'EST_BOOK'];
/** Which delta key is the like-for-like ratio to show under each source column. */
const RATIO_KEY = {
  OTTAV_61075: 'OTTAV_61075',
  OTTAV_LATEST: 'OTTAV_LATEST',
  EST_BOOK: 'EST_BOOK',
  MCAA: 'MCAA_vs_PRDB_MCAA',
};

export default function ComparisonHub({ aiAvailable, canEdit }) {
  const { instance, accounts } = useMsal();
  const [subTab, setSubTab] = useState('table');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [dims, setDims] = useState(null);
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('deviation');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, search: searchInput.trim() })), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    apiGet(instance, accounts, '/rate-benchmarks/comparison/dimensions')
      .then((r) => setDims(r.data))
      .catch(() => {});
  }, [instance, accounts]);

  const filterParams = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== false && v !== undefined) params.set(k, String(v));
    });
    return params;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filterParams();
      params.set('page', String(page));
      params.set('pageSize', '100');
      params.set('sortBy', sortBy);
      const r = await apiGet(instance, accounts, `/rate-benchmarks/comparison?${params}`);
      setResult(r);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, filterParams, page, sortBy]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearchInput(''); setPage(1); };
  const hasFilters = Object.entries(filters).some(([, v]) => v !== '' && v !== false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = filterParams();
      const res = await authenticatedApiCall(
        instance, accounts, 'GET', `/rate-benchmarks/export/comparison?${params}`,
        null, { responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rate-benchmark-comparison-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const dimSelect = (key, label) => (
    <select style={styles.select} value={filters[key]} onChange={(e) => setFilter(key, e.target.value)}>
      <option value="">{label}: all</option>
      {(dims?.[key] || []).slice(0, 200).map((o) => (
        <option key={o.v} value={o.v}>{o.v} ({o.n})</option>
      ))}
    </select>
  );

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
        <input
          style={{ ...styles.input, width: 180 }}
          placeholder="Search…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {dimSelect('description', 'Description')}
        {dimSelect('material', 'Material')}
        {dimSelect('sch', 'Sch')}
        {dimSelect('rating', 'Rating')}
        <input style={{ ...styles.input, width: 76 }} type="number" placeholder='Size ≥ "' value={filters.sizeMin} onChange={(e) => setFilter('sizeMin', e.target.value)} />
        <input style={{ ...styles.input, width: 76 }} type="number" placeholder='Size ≤ "' value={filters.sizeMax} onChange={(e) => setFilter('sizeMax', e.target.value)} />
        <select style={styles.select} value={filters.minCoverage} onChange={(e) => setFilter('minCoverage', e.target.value)}>
          <option value="">Coverage: any</option>
          {[2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>≥ {n} sources</option>)}
        </select>
        <select style={styles.select} value={filters.onlyGapsIn} onChange={(e) => setFilter('onlyGapsIn', e.target.value)}>
          <option value="">Gaps: off</option>
          {TABLE_SOURCES.map((s) => <option key={s} value={s}>Gaps in {SOURCE_LABELS[s]}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: '#374151' }}>
          <input type="checkbox" checked={filters.excludeReducers} onChange={(e) => setFilter('excludeReducers', e.target.checked)} />
          Hide reducing sizes
        </label>
        {hasFilters && <button style={styles.btnGhost} onClick={clearFilters}>Clear</button>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <select style={styles.select} value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
            <option value="deviation">Sort: biggest deviation</option>
            <option value="coverage">Sort: coverage</option>
            <option value="description">Sort: description</option>
          </select>
          <button style={styles.btnGhost} disabled={exporting} onClick={exportExcel}>
            {exporting ? 'Exporting…' : '⬇ Excel'}
          </button>
        </div>
      </div>

      {aiAvailable && <AiInsight filterParams={filterParams} />}

      {error && <ErrorBox error={error} onRetry={load} />}
      {loading && !result && <Spinner label="Building comparison (first load joins all sources)…" />}

      {result && (
        <>
          <StatsPanel stats={result.stats} meta={result.meta} />
          <div style={{ display: 'flex', gap: 4, margin: '10px 0' }}>
            <button style={{ ...(subTab === 'table' ? styles.btn : styles.btnGhost), padding: '4px 12px' }} onClick={() => setSubTab('table')}>
              Rows ({fmtInt(result.total)})
            </button>
            <button style={{ ...(subTab === 'unmatched' ? styles.btn : styles.btnGhost), padding: '4px 12px' }} onClick={() => setSubTab('unmatched')}>
              Unmatched &amp; Crosswalk
            </button>
          </div>

          {subTab === 'table' && (
            <ComparisonTable
              result={result}
              loading={loading}
              page={page}
              setPage={setPage}
            />
          )}
          {subTab === 'unmatched' && (
            <UnmatchedPanel filterParams={filterParams} canEdit={canEdit} onMappingChanged={load} />
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI insight (filter-aware, debounced)
// ---------------------------------------------------------------------------
function AiInsight({ filterParams }) {
  const { instance, accounts } = useMsal();
  const [state, setState] = useState({ status: 'loading', text: '', cached: false });
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = ++requestSeq.current;
    setState((s) => ({ ...s, status: 'loading' }));
    const t = setTimeout(() => {
      apiGet(instance, accounts, `/rate-benchmarks/comparison/insight?${filterParams()}`)
        .then((r) => {
          if (requestSeq.current !== seq) return;
          if (r.text) setState({ status: 'ready', text: r.text, cached: !!r.cached, usedAi: !!r.usedAi });
          else setState({ status: 'unavailable', text: '' });
        })
        .catch(() => {
          if (requestSeq.current === seq) setState({ status: 'error', text: '' });
        });
    }, 700); // debounce filter churn
    return () => clearTimeout(t);
  }, [instance, accounts, filterParams]);

  if (state.status === 'unavailable') return null;
  return (
    <div style={{
      ...styles.card, marginBottom: 12, borderLeft: `4px solid ${ACCENT}`,
      background: 'linear-gradient(180deg, #f0fdfa 0%, #ffffff 100%)',
    }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
        🤖 AI insight — current filter
      </div>
      {state.status === 'loading' && <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>Reading the numbers…</div>}
      {state.status === 'error' && <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Insight unavailable right now.</div>}
      {state.status === 'ready' && (
        <div style={{ fontSize: '0.85rem', lineHeight: 1.55, color: '#1f2937' }}>{state.text}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aggregate stats
// ---------------------------------------------------------------------------
function StatsPanel({ stats, meta }) {
  if (!stats) return null;
  const pw = stats.pairwiseVsPartRateDb || {};
  const PAIR_ROWS = [
    ['OTTAV_61075', 'Ottav 61075 vs OTT Hour'],
    ['OTTAV_LATEST', 'Ottav Latest vs OTT Hour'],
    ['EST_BOOK', 'Estimating Book vs OTT Hour'],
    ['MCAA_vs_PRDB_MCAA', 'MCAA book vs Part Rate DB MCAA column'],
    ['MCAA', 'MCAA book vs OTT Hour (methodology differs)'],
  ];
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
      <div style={{ ...styles.card, flex: '1 1 300px' }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Coverage ({fmtInt(stats.rowCount)} part-sizes)</div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Source</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Values</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Gaps</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Not covered</th>
            </tr>
          </thead>
          <tbody>
            {TABLE_SOURCES.map((s) => {
              const p = stats.perSource?.[s] || {};
              return (
                <tr key={s}>
                  <td style={{ ...styles.td, color: SOURCE_COLORS[s], fontWeight: 600 }}>{SOURCE_LABELS[s]}</td>
                  <td style={styles.tdNum}>{fmtInt(p.values)}</td>
                  <td style={{ ...styles.tdNum, color: p.gaps ? '#92400e' : undefined }}>{fmtInt(p.gaps)}</td>
                  <td style={{ ...styles.tdNum, color: '#9ca3af' }}>{fmtInt(p.notCovered)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ ...styles.card, flex: '1 1 380px' }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Pairwise agreement (matched pairs only)</div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Pair</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>n</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>within ±5%</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>higher</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>lower</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>median ratio</th>
            </tr>
          </thead>
          <tbody>
            {PAIR_ROWS.filter(([k]) => pw[k]).map(([k, label]) => {
              const p = pw[k];
              return (
                <tr key={k}>
                  <td style={styles.td}>{label}</td>
                  <td style={styles.tdNum}>{fmtInt(p.n)}</td>
                  <td style={{ ...styles.tdNum, color: '#166534' }}>{p.n ? `${((p.within5pct / p.n) * 100).toFixed(0)}%` : ''}</td>
                  <td style={{ ...styles.tdNum, color: '#991b1b' }}>{fmtInt(p.over)}</td>
                  <td style={{ ...styles.tdNum, color: '#1e40af' }}>{fmtInt(p.under)}</td>
                  <td style={{ ...styles.tdNum, fontWeight: 700 }}>{fmtRatio(p.medianRatio)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {meta && (
          <div style={{ marginTop: 6, fontSize: '0.7rem', color: '#9ca3af' }}>
            Dataset built {new Date(meta.builtAt).toLocaleTimeString()} · {fmtInt(meta.totalRows)} joined rows · {fmtInt(meta.ebDerivedCount)} factor-derived · {fmtInt(meta.mappingCount)} crosswalk mappings
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main comparison table
// ---------------------------------------------------------------------------
function ComparisonTable({ result, loading, page, setPage }) {
  return (
    <div>
      <div style={styles.scroll}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, position: 'sticky', left: 0, zIndex: 3 }}>Description</th>
              <th style={styles.th}>Material</th>
              <th style={styles.th}>Sch</th>
              <th style={styles.th}>Rating</th>
              <th style={styles.th}>Size</th>
              <th style={styles.th}>Cov.</th>
              {TABLE_SOURCES.map((s) => (
                <th key={s} style={{ ...styles.th, textAlign: 'right', color: SOURCE_COLORS[s] }}>{SOURCE_LABELS[s]}</th>
              ))}
            </tr>
          </thead>
          <tbody style={{ opacity: loading ? 0.5 : 1 }}>
            {(result.data || []).map((r) => (
              <tr key={r.key}>
                <td style={{ ...styles.td, position: 'sticky', left: 0, background: '#fff', fontWeight: 600, zIndex: 1 }}>
                  {r.description}
                  {r.endTypes?.length ? <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.7rem' }}> [{r.endTypes.join('/')}]</span> : null}
                </td>
                <td style={styles.td}>{r.material}</td>
                <td style={styles.td}>{r.sch === 'NA' ? '' : r.sch}</td>
                <td style={styles.td}>{r.rating === 'NA' ? '' : r.rating}</td>
                <td style={styles.td}>{r.size}"</td>
                <td style={styles.td}>
                  <span style={styles.badge(r.coverage >= 4 ? '#dcfce7' : r.coverage >= 2 ? '#e0f2fe' : '#f3f4f6', r.coverage >= 4 ? '#166534' : r.coverage >= 2 ? '#075985' : '#6b7280')}>
                    {r.coverage}/6
                  </span>
                </td>
                {TABLE_SOURCES.map((s) => {
                  const ratioKey = RATIO_KEY[s];
                  const ratio = ratioKey && r.deltas?.[ratioKey] ? r.deltas[ratioKey].ratio : null;
                  return (
                    <td key={s} style={styles.tdNum}>
                      <SourceCell cell={r.sources[s]} />
                      {ratio !== null && ratio !== undefined && Math.abs(ratio - 1) > 0.0005 ? (
                        <span style={{ marginLeft: 5 }}><RatioBadge ratio={ratio} /></span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={styles.btnGhost} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Page {result.page} of {result.pageCount || 1} · {fmtInt(result.total)} rows</span>
        <button style={styles.btnGhost} disabled={page >= (result.pageCount || 1)} onClick={() => setPage((p) => p + 1)}>Next →</button>
        <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#9ca3af' }}>
          <sup style={{ color: '#be123c', fontWeight: 700 }}>f</sup> factor-derived ·{' '}
          <sup>*</sup> schedule wildcard · <sup>~</sup> schedule average ·{' '}
          <span style={styles.badge('#fef3c7', '#92400e')}>gap</span> family covered, size missing · — not covered
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unmatched + crosswalk mappings
// ---------------------------------------------------------------------------
function UnmatchedPanel({ filterParams, canEdit, onMappingChanged }) {
  const { instance, accounts } = useMsal();
  const [unmatched, setUnmatched] = useState(null);
  const [mappings, setMappings] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ source_code: '*', field: 'description', raw_value: '', canonical_value: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    Promise.all([
      apiGet(instance, accounts, `/rate-benchmarks/comparison/unmatched?${filterParams()}`),
      apiGet(instance, accounts, '/rate-benchmarks/mappings'),
    ])
      .then(([u, m]) => { setUnmatched(u.data); setMappings(m.data || []); })
      .catch((e) => setError(errMsg(e)));
  }, [instance, accounts, filterParams]);

  useEffect(() => { load(); }, [load]);

  const saveMapping = async () => {
    if (!form.raw_value.trim() || !form.canonical_value.trim()) return;
    setBusy(true);
    try {
      await apiSend(instance, accounts, 'POST', '/rate-benchmarks/mappings', {
        ...form, raw_value: form.raw_value.trim(), canonical_value: form.canonical_value.trim(),
      });
      setForm((f) => ({ ...f, raw_value: '', canonical_value: '' }));
      load();
      onMappingChanged();
    } catch (e) {
      alert(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteMapping = async (m) => {
    if (!window.confirm(`Remove mapping "${m.raw_value}" → "${m.canonical_value}"?`)) return;
    setBusy(true);
    try {
      await apiSend(instance, accounts, 'DELETE', `/rate-benchmarks/mappings/${m.id}`);
      load();
      onMappingChanged();
    } catch (e) {
      alert(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorBox error={error} onRetry={load} />;
  if (!unmatched) return <Spinner label="Analyzing unmatched families…" />;

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ flex: '2 1 480px' }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>
          Part families with no counterpart
          <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.75rem', marginLeft: 8 }}>
            spelling mismatch → add a crosswalk mapping · real gap → Part Rate DB extension candidate
          </span>
        </div>
        {Object.entries(unmatched).map(([src, fams]) => (
          fams && fams.length ? (
            <details key={src} open={src === 'PART_RATE_DB'} style={{ ...styles.card, marginBottom: 8 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: SOURCE_COLORS[src] || '#374151' }}>
                {SOURCE_LABELS[src] || src} — {fmtInt(fams.length)} families
                {src === 'PART_RATE_DB' ? ' (in Part Rate DB but in no benchmark)' : ' (in this benchmark but not in Part Rate DB)'}
              </summary>
              <div style={{ maxHeight: 300, overflow: 'auto', marginTop: 6 }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Description</th>
                      <th style={styles.th}>Material</th>
                      <th style={styles.th}>Sch</th>
                      <th style={styles.th}>Rating</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Sizes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fams.slice(0, 300).map((f, i) => (
                      <tr key={i}>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{f.description}</td>
                        <td style={styles.td}>{f.material}</td>
                        <td style={styles.td}>{f.sch === 'NA' ? '' : f.sch}</td>
                        <td style={styles.td}>{f.rating === 'NA' ? '' : f.rating}</td>
                        <td style={styles.tdNum}>{fmtInt(f.sizes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : null
        ))}
      </div>

      <div style={{ flex: '1 1 340px' }}>
        <div style={{ ...styles.card, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Crosswalk mappings</div>
          {canEdit ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <select style={{ ...styles.select, flex: 1 }} value={form.source_code} onChange={(e) => setForm((f) => ({ ...f, source_code: e.target.value }))}>
                  <option value="*">Any source</option>
                  <option value="PART_RATE_DB">Part Rate DB</option>
                  <option value="OTTAV_61075">Ottav 61075</option>
                  <option value="OTTAV_LATEST">Ottav Latest</option>
                  <option value="MCAA">MCAA</option>
                  <option value="EST_BOOK">Estimating Book</option>
                </select>
                <select style={{ ...styles.select, flex: 1 }} value={form.field} onChange={(e) => setForm((f) => ({ ...f, field: e.target.value }))}>
                  <option value="description">Description</option>
                  <option value="material">Material</option>
                  <option value="sch">Schedule</option>
                </select>
              </div>
              <input style={styles.input} placeholder="Source spelling (raw value)" value={form.raw_value} onChange={(e) => setForm((f) => ({ ...f, raw_value: e.target.value }))} />
              <input style={styles.input} placeholder="Canonical value (Part Rate DB vocabulary)" value={form.canonical_value} onChange={(e) => setForm((f) => ({ ...f, canonical_value: e.target.value }))} />
              <button style={styles.btn} disabled={busy || !form.raw_value.trim() || !form.canonical_value.trim()} onClick={saveMapping}>
                {busy ? 'Saving…' : 'Add / update mapping'}
              </button>
            </div>
          ) : (
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 8 }}>Read-only — mapping edits are restricted.</div>
          )}
          {mappings && mappings.length ? (
            <div style={{ maxHeight: 320, overflow: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Source</th>
                    <th style={styles.th}>Field</th>
                    <th style={styles.th}>Raw → Canonical</th>
                    {canEdit && <th style={styles.th} />}
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m) => (
                    <tr key={m.id}>
                      <td style={styles.td}>{m.source_code}</td>
                      <td style={styles.td}>{m.field}</td>
                      <td style={styles.td}><strong>{m.raw_value}</strong> → {m.canonical_value}</td>
                      {canEdit && (
                        <td style={styles.td}>
                          <button style={{ ...styles.btnGhost, padding: '2px 8px', color: '#b91c1c' }} disabled={busy} onClick={() => deleteMapping(m)}>×</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>No crosswalk mappings yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
